import React, { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { formatMessage } from 'devextreme/localization';
import LoadPanel from 'devextreme-react/load-panel';
import Button from 'devextreme-react/button';

import { apiScopes } from '../../AuthConfig';

import './LoginPage.css';

interface LoginPageProps {
    // Why the previous sign-in attempt failed, if it did.
    errorMessage?: string;
}

/**
 * Screen of an unauthenticated visitor: it goes straight to the regular B2C sign-in (local account or public
 * provider), which asks for the email itself. Companies that sign in through their own identity provider (SAML SSO)
 * never come through here: they use the dedicated URL of their company (see SsoLoginPage), so that the two kinds of
 * accounts stay separate.
 *
 * After a failure the visitor is not redirected again automatically (that would loop): a button retries.
 */
const LoginPage: React.FC<LoginPageProps> = ({ errorMessage }) => {
    const { instance } = useMsal();

    const [failed, setFailed] = useState<boolean>(false);
    const started = useRef<boolean>(false);

    const signIn = async () => {
        setFailed(false);

        try {
            await instance.loginRedirect({ scopes: apiScopes });
        } catch (error) {
            console.error(error);
            setFailed(true);
        }
    };

    useEffect(() => {
        // Strict mode runs effects twice in development: a second redirect would fail with interaction_in_progress.
        if (started.current || errorMessage) {
            return;
        }

        started.current = true;
        signIn();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!errorMessage && !failed) {
        return <LoadPanel visible={true} />;
    }

    return (
        <div className="login-page">
            <div className="login-page-card">
                <h2>{formatMessage('LoginPage_Title')}</h2>

                <div className="login-page-error" role="alert">{errorMessage || formatMessage('LoginPage_SignInFailed')}</div>

                <Button
                    text={formatMessage('LoginPage_SignIn')}
                    type="default"
                    stylingMode="contained"
                    width="100%"
                    onClick={signIn}
                />
            </div>
        </div>
    );
};

export default LoginPage;
